import { Chevron, Icon, useModal } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useTranslator } from '@jutro/locale';
import { ServiceManager } from '@jutro/legacy/services';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import {
    ViewModelForm,
    ViewModelServiceContext,
} from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import config from 'app-config';
import classNames from 'classnames';
import {
    fnolCommonMessages,
    VehicleDamagePicker,
} from 'gw-capability-fnol-common-react';
import { WizardPageTemplate } from 'gw-portals-wizard-components-ui';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import FNOLCAContactItemComponent from '../../components/CAContactItemComponent/CAContactItemComponent';
import messages from '../../FNOLCA.messages';
import metadata from './VehiclesPage.metadata.json5';
import caClaimStyles from './VehiclesPage.module.scss';

import { IconButton, Button, IntlPhoneNumberField, InputField } from '@jutro/legacy/components';

const displayableVehicleProperties = ['licensePlate', 'make', 'model', 'year'];

const newContact = {
    placeholder: true,
};

function FNOLCAVehiclesPage(props) {
    const { showConfirm } = useModal();

    const breakpoint = useContext(BreakpointTrackerContext);
    const localeService = ServiceManager.getService('locale-service');
    const translator = useTranslator();
    const { wizardData: claimVM, updateWizardData } = props;
    const { oldestCarYear } = config.personalAutoConfig;
    const viewModelService = useContext(ViewModelServiceContext);

    const { onValidate, isComponentValid, registerComponentValidation } =
        useValidation('FNOLCAVehiclePage');

    useEffect(() => {
        const vehiclesPath = 'lobs.commercialAuto.vehicles.value';
        const selectedVehicles = _.get(claimVM, vehiclesPath, []);
        const vechicleIncident = _.get(
            claimVM.value,
            'lobs.commercialAuto.vehicleIncidents'
        );

        if (vechicleIncident && vechicleIncident.length === 0) {
            claimVM.lobs.commercialAuto.value.generateVehicleIncidents(
                selectedVehicles
            );
            updateWizardData(claimVM);
        }
        // Call once when page gets rendered
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const writeValue = useCallback(
        (value, path) => {
            const newClaimVM = _.clone(claimVM);

            _.set(newClaimVM, path, value);
            updateWizardData(newClaimVM);
        },
        [claimVM, updateWizardData]
    );

    const getAvailableDrivers = useCallback(
        (selectedContact, index) => {
            const driversItems =
                claimVM.lobs.commercialAuto.vehicleIncidents.value[
                    index
                ].availableDrivers(
                    claimVM.lobs.commercialAuto.value,
                    selectedContact
                );

            driversItems.push(newContact);

            const driverValues = driversItems.map(driverValue => {
                const val = driverValue.placeholder
                    ? translator(messages.caOtherDriver)
                    : driverValue.getDisplayName(
                          translator(messages.caNewDriver)
                      );

                return {
                    code: val,
                    name: val,
                };
            });

            return driverValues;
        },
        [claimVM, translator]
    );

    const getDriverValue = useCallback(
        index => {
            const driverPath = `lobs.commercialAuto.vehicleIncidents.value[${index}].driver`;
            const selectedDriver = _.get(claimVM, driverPath);

            return !_.isEmpty(selectedDriver)
                ? selectedDriver.getDisplayName(
                      translator(messages.caNewDriver)
                  )
                : '';
        },
        [claimVM, translator]
    );

    const getAvailableYears = useCallback(() => {
        const currentYear = new Date().getFullYear();
        const years = [];

        for (let i = currentYear; i >= oldestCarYear; i -= 1) {
            years.push({
                code: i,
                name: i,
            });
        }

        return years;
    }, [oldestCarYear]);

    const handleDriverSelect = useCallback(
        (value, selectedIndex) => {
            let selectedDriver = {};

            if (value === translator(messages.caOtherDriver)) {
                const areAllDriversValid = _.every(
                    _.get(claimVM.value, 'contacts'),
                    contact =>
                        contact.firstName !== undefined &&
                        contact.lastName !== undefined
                );

                if (areAllDriversValid) {
                    const vehicleIncidentPath = `lobs.commercialAuto.vehicleIncidents.value[${selectedIndex}]`;
                    const vehicleIncident = _.get(claimVM, vehicleIncidentPath);

                    vehicleIncident.setNewDriver(claimVM.value);
                    updateWizardData(claimVM);
                }
            } else {
                const driversItems = _.get(claimVM, 'contacts.value', []);

                selectedDriver = driversItems.find(driver => {
                    const driverName = driver.getDisplayName(
                        translator(messages.caNewDriver)
                    );

                    return driverName === value;
                });

                if (!_.isEmpty(selectedDriver)) {
                    _.set(
                        claimVM,
                        `lobs.commercialAuto.vehicleIncidents.value[${selectedIndex}].driver`,
                        selectedDriver
                    );
                } else {
                    _.set(
                        claimVM,
                        `lobs.commercialAuto.vehicleIncidents.value[${selectedIndex}].driver`,
                        null
                    );
                }

                updateWizardData(claimVM);
            }
        },
        [claimVM, translator, updateWizardData]
    );

    const createTypeList = useCallback(() => {
        let typeList = viewModelService.create(
            {},
            'cc',
            'edge.capabilities.claim.lob.impl.commonauto.dto.VehicleIncidentDTO'
        );

        typeList = typeList.collisionPoint.aspects.availableValues[0].typelist;

        return typeList;
    }, [viewModelService]);

    const renderTriggerAddPerson = useCallback(
        (dropDownProps, toggleMenu) => {
            const { isOpen, ref } = dropDownProps;
            const onAddPersonClick = () => {
                toggleMenu(!isOpen);
            };

            return (
                <Button
                    onClick={onAddPersonClick}
                    icon="mi-expand-more"
                    iconPosition="right"
                    ref={ref}
                >
                    {translator(messages.caAddPassenger)}
                </Button>
            );
        },
        [translator]
    );

    const generatePassengerTableRow = useCallback(
        (evt, incidentIndex, index) => {
            evt.preventDefault();

            const getCurrentMenuItemID = evt.currentTarget.id;
            const getCurrentMenuItemIDNew = getCurrentMenuItemID.split('_');
            const contactsPath = 'contacts.value';
            const contactsItems = _.get(claimVM, contactsPath, []);

            if (
                getCurrentMenuItemIDNew[0].indexOf('anotherPersonLink') === -1
            ) {
                const selectedPassenger = contactsItems[index];

                if (_.filter(contactsItems, selectedPassenger).length) {
                    claimVM.lobs.commercialAuto.vehicleIncidents.value[
                        incidentIndex
                    ].addPassenger(selectedPassenger, claimVM.value);
                }
            } else {
                claimVM.lobs.commercialAuto.vehicleIncidents.value[
                    incidentIndex
                ].addPassenger(null, claimVM.value);
            }

            updateWizardData(claimVM);
        },
        [claimVM, updateWizardData]
    );

    const handleVehicleIncidentDelete = useCallback(
        (event, vehicleIncident) => {
            event.stopPropagation();
            showConfirm({
                title: messages.caDeleteVehicleTitle,
                message: messages.caDeleteVehicleMessage,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: messages.caResponseYes,
                cancelButtonText: messages.caResponseNo,
            }).then(results => {
                if (results === 'cancel' || results === 'close') {
                    return _.noop();
                }

                claimVM.lobs.commercialAuto.value.removeVehicleIncident(
                    vehicleIncident
                );
                updateWizardData(claimVM);

                return true;
            }, _.noop);
        },
        [claimVM, updateWizardData, showConfirm]
    );

    const renderAccordionHeader = useCallback(
        (isOpen, vehicleIncident) => {
            const { vehicle } = vehicleIncident;
            const tabHeading = displayableVehicleProperties
                .filter(prop => vehicle[prop])
                .map(prop => vehicle[prop])
                .join(' ');
            const vehicleAccordionTitle = _.isEmpty(tabHeading)
                ? vehicleIncident.vehicle.getDisplayName(
                      translator(messages.caNewVehicle)
                  )
                : tabHeading;
            const containerStyles = classNames(
                'wizardTitle',
                caClaimStyles.accordionHeader,
                caClaimStyles.accordionHeaderSeparater
            );

            return (
                <React.Fragment>
                    <Chevron
                        isOpen={isOpen}
                        className={caClaimStyles.chevronStyle}
                    />
                    <div className={containerStyles}>
                        <div className={caClaimStyles.accordionHeader}>
                            <Icon icon="mi-directions_car" />
                            <h3 className={caClaimStyles.accordionHeaderTitle}>
                                {vehicleAccordionTitle}
                            </h3>
                        </div>
                        {!vehicleIncident.vehicle.policyVehicle ? (
                            <IconButton
                                icon="mi-delete"
                                value={vehicleIncident}
                                className={
                                    caClaimStyles.caVehicleIncidentDeleteIcon
                                }
                                onClick={event => {
                                    handleVehicleIncidentDelete(
                                        event,
                                        vehicleIncident
                                    );
                                }}
                            />
                        ) : null}
                    </div>
                </React.Fragment>
            );
        },
        [handleVehicleIncidentDelete, translator]
    );

    const generateVehicleOverrides = useCallback(() => {
        const vehicleIncidentsPath =
            'lobs.commercialAuto.vehicleIncidents.value';
        const vehicleIncidents = _.get(claimVM, vehicleIncidentsPath, []);
        const overrides = vehicleIncidents.map((vehicleIncident, index) => {
            if (
                _.isEmpty(vehicleIncident.vehicle) ||
                _.isNull(vehicleIncident.vehicle)
            ) {
                const defaultCountryCode =
                    localeService.getDefaultCountryCode();
                const newVehicle =
                    claimVM.lobs.commercialAuto.value.createVehicle({
                        country: defaultCountryCode,
                    });

                _.set(vehicleIncident, 'vehicle', newVehicle);
                updateWizardData(claimVM);
            }

            return {
                [`caVehicleAccordionDetails${index}`]: {
                    renderHeader: isOpen =>
                        renderAccordionHeader(isOpen, vehicleIncident),
                },
                [`caOtherDriverSection${index}`]: {
                    visible:
                        !_.isEmpty(
                            _.get(
                                claimVM,
                                `lobs.commercialAuto.vehicleIncidents.children[${index}].driver.value`
                            )
                        ) &&
                        !_.get(
                            claimVM,
                            `lobs.commercialAuto.vehicleIncidents.children[${index}].driver.policyRole.value`
                        ),
                },
                [`caVehiclesWhoDriving${index}`]: {
                    availableValues: getAvailableDrivers(
                        vehicleIncident.driver,
                        index
                    ),
                    value: getDriverValue(index),
                    onValueChange: value => {
                        handleDriverSelect(value, index);
                    },
                },
                [`caAddPassengerTable${index}`]: {
                    data: vehicleIncident.passengers,
                    visible: !_.isEmpty(vehicleIncident.passengers),
                },
                [`caPassengersAddPassenger${index}`]: {
                    renderTrigger: renderTriggerAddPerson,
                },
                [`caPointOfImpactSelect${index}`]: {
                    collisionPoints: createTypeList(),
                    onValueChange: writeValue,
                },
                [`caOtherVehicleSection${index}`]: {
                    visible: !vehicleIncident.vehicle.policyVehicle,
                },
                [`caVehicleOtherVehicleYear${index}`]: {
                    availableValues: getAvailableYears(),
                    value: _.get(
                        claimVM,
                        `lobs.commercialAuto.vehicleIncidents.children[${index}].vehicle.year.value`
                    ),
                },
                [`addPersonDropDownItems${index}`]: {
                    data: claimVM,
                    incidentIndex: index,
                    updateAddPassengers: updateWizardData,
                },
                [`anotherPersonLink${index}`]: {
                    onClick: e => {
                        generatePassengerTableRow(e, index);
                    },
                },
            };
        });

        return Object.assign({}, ...overrides);
    }, [
        claimVM,
        createTypeList,
        generatePassengerTableRow,
        getAvailableDrivers,
        getAvailableYears,
        getDriverValue,
        handleDriverSelect,
        localeService,
        renderAccordionHeader,
        renderTriggerAddPerson,
        updateWizardData,
        writeValue,
    ]);

    const getAddPassengerName = (item, index, property) => {
        let [vehicleIncidentIndex] = (property.path || '').match(/\d+/g) || [];

        vehicleIncidentIndex = Number(vehicleIncidentIndex);

        const elementPath = `lobs.commercialAuto.vehicleIncidents.value[${vehicleIncidentIndex}].passengers[${index}].${property.id}`;

        return (
            <InputField
                id={`${property.id}_${vehicleIncidentIndex}_${index}`}
                path={elementPath}
                onValueChange={writeValue}
                value={item[property.id]}
                disabled={!_.isEmpty(item.publicID)}
                hideLabel
            />
        );
    };

    const getAddPassengerPhone = (item, index, property) => {
        let [vehicleIncidentIndex] = (property.path || '').match(/\d+/g) || [];

        vehicleIncidentIndex = Number(vehicleIncidentIndex);

        const elementPath = `lobs.commercialAuto.vehicleIncidents.value[${vehicleIncidentIndex}].passengers[${index}].${property.id}`;

        return (
            <IntlPhoneNumberField
                id={`${property.id}_${vehicleIncidentIndex}_${index}`}
                path={elementPath}
                onValueChange={writeValue}
                dataType="string"
                value={item[property.id]}
                disabled={!_.isEmpty(item.publicID)}
                hideLabel
            />
        );
    };

    const removePassengerItem = useCallback(
        (e, index, incidentIndex) => {
            e.preventDefault();

            const newClaimVM = _.clone(claimVM);

            newClaimVM.lobs.commercialAuto.vehicleIncidents.value[
                incidentIndex
            ].passengers.splice(index, 1);
            updateWizardData(newClaimVM);
        },
        [claimVM, updateWizardData]
    );

    const removePassenger = useCallback(
        (item, index, property) => {
            let [vehicleIncidentIndex] =
                (property.path || '').match(/\d+/g) || [];

            vehicleIncidentIndex = Number(vehicleIncidentIndex);

            return (
                <IconButton
                    icon="mi-delete"
                    onClick={e =>
                        removePassengerItem(e, index, vehicleIncidentIndex)
                    }
                />
            );
        },
        [removePassengerItem]
    );

    const validateTableForm = useCallback(() => {
        if (
            !claimVM.lobs.commercialAuto.vehicleIncidents.aspects.valid ||
            !claimVM.lobs.commercialAuto.vehicleIncidents.aspects.subtreeValid
        ) {
            return false;
        }

        return true;
    }, [claimVM]);

    const addVehicleIncident = useCallback(() => {
        if (claimVM.lobs.commercialAuto.vehicleIncidents.aspects.subtreeValid) {
            claimVM.lobs.commercialAuto.value.createVehicleIncident();
            updateWizardData(claimVM);
        }
    }, [claimVM, updateWizardData]);

    useEffect(() => {
        registerComponentValidation(validateTableForm);
    }, [registerComponentValidation, validateTableForm]);

    const overrides = useMemo(
        () => ({
            '@field': {
                onValueChange: writeValue,
                showOptional: true,
                phoneWide: {
                    labelPosition: 'top',
                },
            },
            caVehicleAccordion: {
                size: breakpoint === 'desktop' ? 'medium' : 'small',
            },
            ...generateVehicleOverrides(),
        }),
        [writeValue, breakpoint, generateVehicleOverrides]
    );

    const resolvers = {
        resolveClassNameMap: caClaimStyles,
        resolveComponentMap: {
            vehiclecollision: VehicleDamagePicker,
            contactitemcomponent: FNOLCAContactItemComponent,
        },
        resolveCallbackMap: {
            getAddPassengerName,
            getAddPassengerPhone,
            removePassenger,
            addVehicleIncident,
        },
    };

    const readValue = useCallback(
        (id, path) =>
            readViewModelValue(
                metadata.pageContent,
                claimVM,
                id,
                path,
                overrides
            ),
        [claimVM, overrides]
    );

    return (
        <WizardPage
            cancelLabel={translator(fnolCommonMessages.fnolSaveandExit)}
            template={WizardPageTemplate}
            disableNext={!isComponentValid}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                resolveValue={readValue}
                overrideProps={overrides}
                onValidationChange={onValidate}
                onModelChange={updateWizardData}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

FNOLCAVehiclesPage.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func,
    }).isRequired,
};

FNOLCAVehiclesPage.propTypes = wizardProps;
export default withRouter(FNOLCAVehiclesPage);
